import {
  CaretDownIcon,
  CheckIcon,
  CircleDashedIcon,
  FilmStripIcon,
  SquaresFourIcon,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { useDialogContentContainer } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { type Placement, toMatrixPlatform } from '@/lib/platform-matrix/placement-matrix';
import {
  type MediaKind,
  PUBLISHABLE_PLACEMENTS,
  allowedPlacements,
  isPlacementOverrideHonored,
  resolvePlacement,
} from '@/lib/platform-matrix/placement-resolution';
import { PLACEMENT_GLYPH, PLACEMENT_LABEL, type PlacementGlyphName } from '@/lib/posts/placement';
import { cn } from '@/lib/utils';

/**
 * One-line copy shown under the label for non-disabled options. Total over
 * `Placement` on purpose — a new placement can't ship a blank description
 * line (compile error instead).
 */
const PLACEMENT_DESCRIPTION: Record<Placement, string> = {
  feed: 'Shows in the main feed',
  reel: 'Short vertical video',
  story: 'Disappears after 24 hours',
  short: 'Short vertical video',
  pin: 'Saved to boards and search',
  landscape: 'Standard video upload',
  document: 'Multi-page document post',
  photo: 'Photo or carousel in the main feed',
};

/**
 * Resolves `PLACEMENT_GLYPH`'s names to the real Phosphor components. Keyed
 * by the `PlacementGlyphName` union so this map and `PLACEMENT_GLYPH` can't
 * drift apart without a compile error.
 */
const ICON_BY_GLYPH_NAME: Record<PlacementGlyphName, Icon> = {
  SquaresFourIcon,
  FilmStripIcon,
  CircleDashedIcon,
};

function iconForPlacement(placement: Placement): Icon {
  return ICON_BY_GLYPH_NAME[PLACEMENT_GLYPH[placement]];
}

export interface PlacementPickerProps {
  platform: string;
  mediaKind: MediaKind;
  value: Placement | undefined;
  onChange: (next: Placement | undefined) => void;
  /** Disables the trigger entirely (e.g. read-only surface). */
  disabled?: boolean;
  /** True while `commitPlacement` (Task A3) is in flight — trigger shows a Spinner and disables. */
  isSaving?: boolean;
  /**
   * Overrides `PUBLISHABLE_PLACEMENTS[platform]` (default). This is how Task
   * A5's `connectionSupportsPlacement` composes — a per-connection publish
   * ceiling narrower or wider than the static table.
   */
  publishablePlacements?: readonly Placement[];
  className?: string;
}

/**
 * Placement dropdown — sibling of `AspectRatioPicker`, same trigger/menu
 * anatomy. Placements not in the effective `publishablePlacements` set are
 * OMITTED entirely (Decision #5 — trust over roadmap, never "coming soon").
 * Placements that can't take the current `mediaKind` are shown disabled with
 * the reason visible inline (never hover-only). Renders `null` when only one
 * placement is allowed (Subtraction).
 */
export const PlacementPicker: React.FC<PlacementPickerProps> = ({
  platform,
  mediaKind,
  value,
  onChange,
  disabled,
  isSaving,
  publishablePlacements,
  className,
}) => {
  const container = useDialogContentContainer();
  const matrixPlatform = toMatrixPlatform(platform);
  const publishable =
    publishablePlacements ?? (matrixPlatform ? PUBLISHABLE_PLACEMENTS[matrixPlatform] : []);

  const options = allowedPlacements(platform, mediaKind).filter((option) =>
    publishable.includes(option.row.placement),
  );

  const derivedRow = resolvePlacement({ platform, mediaKind });

  // An override is honored only when it resolves to itself AND is in the
  // publishable set the menu was just filtered by — the trigger must never
  // claim a placement its own menu omits (Decision #5). Anything else is an
  // ignored override, surfaced with an actionable reset (never silent).
  // `isPlacementOverrideHonored` is the SAME shared check `PostBody`'s
  // preview surface uses (Task A5) — the trigger label and the preview
  // chrome can never disagree on whether a stored override is honored.
  const overrideIsHonored = isPlacementOverrideHonored(platform, mediaKind, value, publishable);
  const overrideWasIgnored = value !== undefined && !overrideIsHonored;
  const resolvedRow = overrideIsHonored
    ? resolvePlacement({ platform, mediaKind, override: value })
    : derivedRow;
  const resolvedPlacement = resolvedRow?.placement;
  const offerResetAction = overrideIsHonored && derivedRow?.placement !== value;

  // Single-option platforms hide the picker (Subtraction) — unless an ignored
  // override needs surfacing, in which case honesty outranks minimalism.
  if (options.length <= 1 && !overrideWasIgnored) return null;

  const TriggerIcon = resolvedPlacement ? iconForPlacement(resolvedPlacement) : SquaresFourIcon;
  const triggerLabel = resolvedPlacement ? PLACEMENT_LABEL[resolvedPlacement] : 'Feed post';
  const isTriggerDisabled = !!disabled || !!isSaving;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isTriggerDisabled}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn('h-7 px-2 gap-1 text-detail', className)}
          >
            {isSaving ? (
              <Spinner size="sm" />
            ) : (
              <TriggerIcon className="size-3.5" weight="regular" />
            )}
            <span>{triggerLabel}</span>
            {!isTriggerDisabled ? <CaretDownIcon className="size-3" weight="bold" /> : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64" container={container}>
          <DropdownMenuLabel className="text-micro uppercase tracking-wider text-muted-foreground">
            Placement
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map(({ row, disabledReason }) => {
            const isCurrent = row.placement === resolvedPlacement;
            const isDisabled = !!disabledReason;
            return (
              <DropdownMenuItem
                key={row.placement}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                onSelect={() => {
                  if (isDisabled) return;
                  onChange(row.placement);
                }}
                className="flex items-start gap-2 py-2"
              >
                <span className="mt-0.5 size-3.5 shrink-0 flex items-center justify-center">
                  {isCurrent ? <CheckIcon className="size-3.5" weight="bold" /> : null}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-foreground">
                    {PLACEMENT_LABEL[row.placement]}
                  </span>
                  <span className="block text-detail text-muted-foreground">
                    {disabledReason ?? PLACEMENT_DESCRIPTION[row.placement]}
                  </span>
                </span>
              </DropdownMenuItem>
            );
          })}
          {offerResetAction && derivedRow ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => onChange(undefined)}
                className="text-detail text-muted-foreground"
              >
                Reset to {PLACEMENT_LABEL[derivedRow.placement]} default
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {overrideWasIgnored ? (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="text-micro text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          Reset to default
        </button>
      ) : null}
    </>
  );
};
